import { AUCTION_INCREMENT_ID_REGEX, DIRECT_SALE_INCREMENT_ID_REGEX, SEARCH_SUGGEST_MIN_QUERY_LENGTH } from '@lib/shared';
import type { PlaceholderDataFunction } from '@tanstack/vue-query';
import type { ComputedRef, Ref } from 'vue';

import {
    type ApiSearchSuggestParams,
    AssetType,
    type BackendErrorEntity,
    type ErrorType,
    getApiSearchSuggestQueryKey,
    type SearchSuggestAuctionItemDto,
    type SearchSuggestDirectSaleItemDto,
    type SearchSuggestResponseDto,
    SellingMethod,
    useApiSearchSuggest,
} from '~/apiClient';
import { type PlatformSellingMethod } from '~/types/sale.type';

// One request per settled query: `/search/suggest` is throttled per IP (`SEARCH_SUGGEST_CONTROLLER_TTL`).
const SEARCH_SUGGEST_DEBOUNCE_MS = 300;

/** The two channels answer with different price fields, so the rows are flattened into one shape. */
export type QuickSearchPreviewItem = {
    id: string;
    slug: string;
    brand: string;
    model: string;
    vin: string;
    coverImage: string | null;
    price: number | null;
    /** Meaningful only on the direct sale channel; auctions never have a price on request. */
    priceOnRequest: boolean;
};

export type QuickSearchPreviewState = {
    items: QuickSearchPreviewItem[];
    /** The full match count for the selected channel, not the number of rows returned. */
    total: number;
    /** Match count on the tab not selected: an empty tab can point there. */
    otherChannelTotal: number;
    /** Drives the skeleton: also true while debouncing, but not while parked offline (it would never stop). */
    isPending: boolean;
    /** The rows on screen answer an older query: debouncing, fetching, or a placeholder (offline included). */
    isStale: boolean;
    isEmpty: boolean;
    isError: boolean;
    query: string;
};

export type UseQuickSearchOptions = {
    onClose?: () => void;
};

export type UseQuickSearchReturn = {
    inputValue: Ref<string>;
    sellingMethod: Ref<PlatformSellingMethod>;
    isLoading: Ref<boolean>;
    preview: ComputedRef<QuickSearchPreviewState>;
    onSubmit: () => Promise<void>;
};

const toPreviewItem = (
    { id, slug, mainAsset }: SearchSuggestAuctionItemDto | SearchSuggestDirectSaleItemDto,
    { price, priceOnRequest }: Pick<QuickSearchPreviewItem, 'price' | 'priceOnRequest'>
): QuickSearchPreviewItem => ({
    id,
    slug,
    brand: mainAsset.brand,
    model: mainAsset.model,
    vin: mainAsset.vin,
    coverImage: mainAsset.coverImage,
    price,
    priceOnRequest,
});

export const useQuickSearch = (options: UseQuickSearchOptions = {}): UseQuickSearchReturn => {
    const localePath = useLocalePath();
    const { formatSlpRoute } = useSlpRouteUtils();
    const { data: authSession } = useAuthUtils();

    // DATA
    const inputValue = ref('');
    const sellingMethod = ref<PlatformSellingMethod>(SellingMethod.DirectSale);
    const isLoading = ref(false);

    const debouncedInput = refDebounced(inputValue, SEARCH_SUGGEST_DEBOUNCE_MS);
    const previewQuery = computed<string>(() => debouncedInput.value.trim());
    // Client-only: the panel renders inside an SSR'd Storyblok hero.
    const isPreviewEnabled = computed<boolean>(() => import.meta.client && previewQuery.value.length >= SEARCH_SUGGEST_MIN_QUERY_LENGTH);
    const suggestParams = computed<ApiSearchSuggestParams>(() => ({ q: previewQuery.value }));

    // The key omits `sellingMethod`: one response carries both channels, so switching tab re-slices the cache.
    // It carries the viewer id instead, because direct sale prices depend on who asks. An id, not a "logged in" flag:
    // `logout()` leaves the cache untouched, so a shared flag would serve the next account the previous one's prices.
    const viewerId = computed<string | null>(() => authSession.value?.user?.id ?? null);
    const queryKey = computed(() => [...getApiSearchSuggestQueryKey(suggestParams.value), viewerId.value]);

    // Keeps the rows up between keystrokes, but never across viewers: the observer outlives a logout wherever the panel
    // stays mounted (the hero), and its placeholder is not scoped by the key, whose last element is the viewer id.
    // A new function per viewer: query-core reuses the last placeholder without calling the function while its identity
    // is unchanged, so a stable arrow would let a hidden placeholder carry the previous viewer's rows across a logout.
    const placeholderData = computed<PlaceholderDataFunction<SearchSuggestResponseDto, ErrorType<BackendErrorEntity>>>(() => {
        const viewer = viewerId.value;

        return (previousData, previousQuery): SearchSuggestResponseDto | undefined =>
            previousQuery?.queryKey.at(-1) === viewer ? previousData : undefined;
    });

    const { data, isError, isFetching, isPaused, isPlaceholderData } = useApiSearchSuggest(suggestParams, {
        query: {
            enabled: isPreviewEnabled,
            queryKey,
            placeholderData,
        },
    });

    const previewResults = computed<Pick<QuickSearchPreviewState, 'items' | 'total' | 'otherChannelTotal'>>(() => {
        // A disabled query keeps serving its placeholder, so this guard is what empties the list.
        if (!isPreviewEnabled.value || !data.value) return { items: [], total: 0, otherChannelTotal: 0 };

        return sellingMethod.value === SellingMethod.Auction
            ? {
                  items: data.value.auctions.map(auction => toPreviewItem(auction, { price: auction.currentPrice, priceOnRequest: false })),
                  total: data.value.auctionsTotal,
                  otherChannelTotal: data.value.directSalesTotal,
              }
            : {
                  items: data.value.directSales.map(directSale =>
                      toPreviewItem(directSale, { price: directSale.buyNowPrice, priceOnRequest: directSale.priceOnRequest })
                  ),
                  total: data.value.directSalesTotal,
                  otherChannelTotal: data.value.auctionsTotal,
              };
    });

    const preview = computed<QuickSearchPreviewState>(() => {
        // Still debouncing: no request is out yet, but the rows answer an older query than the one a submit would send.
        const isDebouncing = inputValue.value.trim() !== previewQuery.value;
        // Offline the query parks at `paused` with the placeholder up: without this the skeleton never stops.
        const isPending = isPreviewEnabled.value && (isDebouncing || isFetching.value || (isPlaceholderData.value && !isPaused.value));
        const isStale = isPreviewEnabled.value && (isDebouncing || isFetching.value || isPlaceholderData.value);

        return {
            ...previewResults.value,
            isPending,
            isStale,
            // Offline with no placeholder the query parks with no data at all: that is not an empty result.
            isEmpty: isPreviewEnabled.value && !isStale && !isError.value && !!data.value && previewResults.value.total === 0,
            isError: isPreviewEnabled.value && isError.value,
            query: previewQuery.value,
        };
    });

    // METHODS
    const onSubmit = async (): Promise<void> => {
        if (isLoading.value) return;

        const trimmed = inputValue.value.trim();

        isLoading.value = true;

        // Below the threshold the SLP would drop the `q` anyway: leave it out and open the whole listing.
        const query = trimmed.length >= SEARCH_SUGGEST_MIN_QUERY_LENGTH ? { [SLP_QUERY_KEYS.q]: trimmed } : {};
        const target = formatSlpRoute({ sellingMethod: sellingMethod.value, assetType: AssetType.Bus }, query);

        try {
            // Also closes on a `duplicated` NavigationFailure, where leaving the panel open is a dead end.
            await navigateTo(localePath(target));

            options.onClose?.();
        } finally {
            isLoading.value = false;
        }
    };

    // WATCHERS
    // A listing code names its channel. Follow it, or the other tab shows an empty preview and Enter lands on an empty
    // SLP: each listing only fast-paths its own prefix. On every edit, not only when the channel changes: a code pasted
    // over another of the same channel must still beat a tab picked by hand, and a code's other tab is always empty.
    watch(
        () => inputValue.value.trim(),
        trimmed => {
            if (AUCTION_INCREMENT_ID_REGEX.test(trimmed)) sellingMethod.value = SellingMethod.Auction;
            else if (DIRECT_SALE_INCREMENT_ID_REGEX.test(trimmed)) sellingMethod.value = SellingMethod.DirectSale;
        }
    );

    return {
        inputValue,
        sellingMethod,
        isLoading,
        preview,
        onSubmit,
    };
};
